import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SeoContent from '../components/SeoContent';
import PageSEO from '../components/PageSEO';

const CATEGORIES = {
  Animals:   ['ELEPHANT','GIRAFFE','PENGUIN','DOLPHIN','KANGAROO','TIGER','LEOPARD','FLAMINGO'],
  Fruits:    ['APPLE','BANANA','CHERRY','MANGO','ORANGE','GRAPE','KIWI','PEACH','LEMON','MELON'],
  Colors:    ['RED','BLUE','GREEN','YELLOW','PURPLE','ORANGE','PINK','BLACK','WHITE','BROWN'],
  Countries: ['FRANCE','JAPAN','BRAZIL','INDIA','CHINA','SPAIN','ITALY','EGYPT','CANADA','RUSSIA'],
  Sports:    ['SOCCER','TENNIS','HOCKEY','BOXING','SWIMMING','CYCLING','CRICKET','ARCHERY'],
  Space:     ['GALAXY','METEOR','PLANET','SATURN','JUPITER','COMET','ROCKET','ASTRONAUT'],
};

const DIRS = [[0,1],[1,0],[1,1],[1,-1],[0,-1],[-1,0],[-1,-1],[-1,1]];
const SIZE  = 14;

function makeGrid(words) {
  const grid = Array.from({length:SIZE}, () => Array(SIZE).fill(''));
  const placed = [];
  for (const word of words) {
    let ok = false;
    for (let attempt = 0; attempt < 200 && !ok; attempt++) {
      const [dr, dc] = DIRS[Math.floor(Math.random() * DIRS.length)];
      const r = Math.floor(Math.random() * SIZE);
      const c = Math.floor(Math.random() * SIZE);
      const er = r + dr * (word.length - 1);
      const ec = c + dc * (word.length - 1);
      if (er < 0 || er >= SIZE || ec < 0 || ec >= SIZE) continue;
      let conflict = false;
      for (let i = 0; i < word.length; i++) {
        const ch = grid[r + dr*i][c + dc*i];
        if (ch !== '' && ch !== word[i]) { conflict = true; break; }
      }
      if (conflict) continue;
      for (let i = 0; i < word.length; i++) grid[r + dr*i][c + dc*i] = word[i];
      placed.push({ word, r, c, dr, dc });
      ok = true;
    }
  }
  const alpha = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  for (let r = 0; r < SIZE; r++)
    for (let c = 0; c < SIZE; c++)
      if (!grid[r][c]) grid[r][c] = alpha[Math.floor(Math.random()*26)];
  return { grid, placed };
}

function isStraightLine(cells) {
  if (cells.length < 2) return true;
  const dr = Math.sign(cells[1].r - cells[0].r);
  const dc = Math.sign(cells[1].c - cells[0].c);
  return cells.every((cell, i) =>
    i === 0 || (cell.r === cells[0].r + dr*i && cell.c === cells[0].c + dc*i)
  );
}

function getCellsBetween(start, end) {
  if (!start || !end) return [];
  const dr = Math.sign(end.r - start.r);
  const dc = Math.sign(end.c - start.c);
  if (dr === 0 && dc === 0) return [start];
  const len = Math.max(Math.abs(end.r - start.r), Math.abs(end.c - start.c));
  return Array.from({length: len + 1}, (_, i) => ({r: start.r + dr*i, c: start.c + dc*i}));
}

export default function WordSearch() {
  const [category, setCategory] = useState('Animals');
  const [puzzle, setPuzzle]     = useState(null);
  const [found, setFound]       = useState(new Set());
  const [selecting, setSelecting] = useState(false);
  const [selStart, setSelStart] = useState(null);
  const [selEnd, setSelEnd]     = useState(null);

  function startGame(cat) {
    setCategory(cat);
    const words = CATEGORIES[cat].slice(0, 8);
    setPuzzle(makeGrid(words));
    setFound(new Set());
    setSelStart(null); setSelEnd(null); setSelecting(false);
  }

  const selCells = selStart && selEnd ? getCellsBetween(selStart, selEnd) : [];
  const isValid  = isStraightLine(selCells);

  function handleCellMouseDown(r, c) {
    setSelecting(true); setSelStart({r,c}); setSelEnd({r,c});
  }

  function handleCellMouseEnter(r, c) {
    if (selecting) setSelEnd({r,c});
  }

  function handleMouseUp() {
    if (!selecting || !puzzle) { setSelecting(false); return; }
    setSelecting(false);
    if (!isValid || selCells.length < 2) { setSelStart(null); setSelEnd(null); return; }
    const selected = selCells.map(({r,c}) => puzzle.grid[r][c]).join('');
    const selRev   = selected.split('').reverse().join('');
    for (const p of puzzle.placed) {
      if (!found.has(p.word) && (p.word === selected || p.word === selRev)) {
        setFound(new Set([...found, p.word]));
        break;
      }
    }
    setSelStart(null); setSelEnd(null);
  }

  function isCellFound(r, c) {
    if (!puzzle) return false;
    return puzzle.placed.some(p => {
      if (!found.has(p.word)) return false;
      for (let i = 0; i < p.word.length; i++)
        if (p.r + p.dr*i === r && p.c + p.dc*i === c) return true;
      return false;
    });
  }

  function isCellSelected(r, c) {
    return isValid && selCells.some(cell => cell.r === r && cell.c === c);
  }

  const allFound = puzzle && found.size === puzzle.placed.length;

  return (
    <div className="game-page">
      <PageSEO page="word-search" />
      <div className="container-fluid" style={{maxWidth:900}}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">🔎</span>
          <h1>Word Search</h1>
          <p>Find all the hidden words! Click and drag to select letters.</p>
        </div>

        {/* Category picker */}
        <div className="solver-card mb-3">
          <p className="fw-bold mb-2">Choose a Category:</p>
          <div className="d-flex flex-wrap gap-2">
            {Object.keys(CATEGORIES).map(cat => (
              <button
                key={cat} type="button"
                className="btn fw-bold"
                style={{
                  borderRadius:'50px', border:'2px solid',
                  borderColor: category===cat ? 'var(--primary)' : 'var(--border)',
                  background: category===cat ? 'var(--primary)' : '#fff',
                  color: category===cat ? '#fff' : 'var(--muted)',
                  fontFamily:'Nunito,sans-serif', padding:'0.4rem 1rem',
                  transition:'all 0.2s',
                }}
                onClick={() => startGame(cat)}
              >{cat}</button>
            ))}
          </div>
          {!puzzle && (
            <button className="solve-btn mt-3" onClick={() => startGame(category)}>
              🎮 Start Word Search
            </button>
          )}
        </div>

        {puzzle && (
          <div className="row g-3">
            {/* Grid */}
            <div className="col-12 col-md-8">
              <div className="solver-card">
                {allFound && (
                  <div className="alert alert-custom mb-3" style={{background:'#d1fae5',color:'#065f46'}}>
                    🎉 Amazing! You found all {puzzle.placed.length} words!
                  </div>
                )}
                <div
                  className="ws-grid"
                  style={{gridTemplateColumns:`repeat(${SIZE},1fr)`,userSelect:'none'}}
                  onMouseLeave={() => { if (selecting) handleMouseUp(); }}
                >
                  {puzzle.grid.map((row, r) => row.map((cell, c) => {
                    const isF = isCellFound(r, c);
                    const isS = isCellSelected(r, c);
                    return (
                      <div
                        key={`${r}-${c}`}
                        className={`ws-cell ${isF ? 'found' : ''} ${isS && !isF ? 'selected' : ''}`}
                        onMouseDown={() => handleCellMouseDown(r,c)}
                        onMouseEnter={() => handleCellMouseEnter(r,c)}
                        onMouseUp={handleMouseUp}
                      >{cell}</div>
                    );
                  }))}
                </div>
              </div>
            </div>

            {/* Word list */}
            <div className="col-12 col-md-4">
              <div className="solver-card">
                <h6 className="fw-bold mb-3">Find these {puzzle.placed.length} words:</h6>
                <ul className="ws-word-list">
                  {puzzle.placed.map(p => (
                    <li key={p.word} className={found.has(p.word) ? 'done' : ''}>
                      {found.has(p.word) ? '✅ ' : '⬜ '}{p.word}
                    </li>
                  ))}
                </ul>
                <div className="mt-3">
                  <small className="text-muted fw-bold">{found.size} of {puzzle.placed.length} found</small>
                  <div className="progress mt-1" style={{height:8,borderRadius:10}}>
                    <div
                      className="progress-bar"
                      style={{width:`${(found.size/puzzle.placed.length)*100}%`,background:'var(--primary)',borderRadius:10}}
                    ></div>
                  </div>
                </div>
                <button className="solve-btn mt-3 w-100" onClick={() => startGame(category)}>
                  🔄 New Puzzle
                </button>
              </div>
            </div>
          </div>
        )}
      <SeoContent game="word-search" />
      </div>
    </div>
  );
}
